<template>
  <div class="tw:w-full tw:flex tw:flex-col tw:md:flex-row">
    <div class="tw:w-full">
      <p class="tw:font-bold tw:text-xl tw:text-gray-800 tw:my-2">{{ $t('app_hotel_details_contact_information') }}</p>
      <div class="tw:flex tw:flex-col tw:gap-2">
        <BaseTextField
          required
          :label="$t('form.labels.email_address')"
          name="email"
          aria-label="email"
          type="text"
          class="tw:w-full tw:mb-4 tw:md:mb-auto"
          size="medium"
          :error="errors['email'] ? $t(errors['email']) : false"
          :value="email"
          @onChange="e => setValue('email', e)"
        />
        <BaseTextField
          :label="$t('app_hotel_details_hotel_website')"
          name="homepageUrl"
          aria-label="homepageUrl"
          type="text"
          class="tw:w-full tw:mb-4 tw:md:mb-auto"
          size="medium"
          :error="errors['homepageUrl'] ? $t(errors['homepageUrl']) : false"
          :value="web"
          @onChange="e => setValue('homepageUrl', e)"
        />
      </div>
    </div>
    <div class="divider tw:hidden tw:md:flex tw:mx-4" />
    <div class="tw:w-full">
      <div class="tw:flex tw:gap-2 tw:mt-4 tw:mb-3 tw:flex-col tw:md:mt-[44px]">
        <div class="tw:w-full">
          <BaseSelect
            :label="$t('form.labels.phone_number')"
            name="phoneRegionCode"
            required
            :display-text-key="'label'"
            :value-key="'value'"
            :options="phoneCodes"
            :value="selectedPhoneLocale"
            version="light"
            size="medium"
            :error="
              errors['phone'] === 'error_field_required' || selectedPhoneLocale === null
                ? $t('error_field_required')
                : false
            "
            @onChange="e => onSelect('phoneRegionCode', e)"
            :dataTestId="`onboarding-phone-code-select-component`"
            :selectTestId="`onboarding-phone-code-select`"
          />
        </div>
        <div class="tw:w-full">
          <BaseTextField
            name="phone"
            aria-label="phone"
            required
            type="tel"
            :max-length="150"
            size="medium"
            :error="errors['phone'] ? getErrorMessage($t(errors['phone']), 'phone') : false"
            :value="phoneNumber"
            @onChange="e => setValue('phoneNumber', e)"
            @onInput="e => inputChange('phoneNumber', e)"
            @keypress="onKeyPress"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { global } from '@/plugins/i18n'
import { validate, required, emailAddress, minLength, latinChars, url } from '@/utils/propertyDetailsHelpers.ts'
import { usePropertyDetailsBasicInfo } from '@/components/propertyDetails/propertyBasicInfo/usePropertyDetailsBasicInfo.js'

import BaseTextField from '@/components/base/inputs/BaseTextField.vue'
import OrganisationManagementService from '@/services/OrganisationManagementService'
import BaseSelect from '@/components/BaseSelect.vue'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  phoneLocale: String,
  errors: {
    type: Object,
    default: () => {},
  },
  email: String,
  web: String,
  phone: {
    type: String,
    default: '',
  },
})
const { language } = useSessionValues()
const phoneNumber = ref('')
const exampleNumber = ref('')
const selectedPhoneLocale = ref(props.phoneLocale)
const phoneValidationErrors = ref([])
const currentValidation = ref('')
const { setNewPropertyValues, setValidation, setValidationInProgress, setPhoneNumberTouched } =
  usePropertyDetailsBasicInfo()

const phoneCodes = computed(() => {
  const phoneCodes = global.getLocaleMessage(language.value).form?.phone_codes ?? {}
  const phoneCodesFormattedForSelect = Object.keys(phoneCodes)?.map(code => ({
    value: code,
    label: phoneCodes[code],
  }))
  const collator = new Intl.Collator('en', { sensitivity: 'base' })
  phoneCodesFormattedForSelect?.sort((a, b) => collator.compare(a.label, b.label))
  return phoneCodesFormattedForSelect
})
const currentPhoneCode = computed(() => phoneCodes.value.find(item => item.value === selectedPhoneLocale.value))

const removeCountryCode = (phoneNumber, countryString) => {
  if (!phoneNumber || !countryString) return phoneNumber

  const match = countryString.label.match(/\(\+(\d+)\)/)
  if (!match) return phoneNumber

  const countryCode = match[1]

  if (phoneNumber.startsWith(`+${countryCode}`)) {
    return phoneNumber.slice(countryCode.length + 1)
  }
  return phoneNumber
}

const combinePhoneNumber = (phoneNumber, countryCodeObj) => {
  if (!phoneNumber || !countryCodeObj) return phoneNumber

  const match = countryCodeObj.label.match(/\(\+(\d+)\)/)
  if (!match) return phoneNumber

  const countryCode = match[1]
  const cleanNumber = phoneNumber.trim()

  return `+${countryCode}${cleanNumber}`
}

watch(
  () => props.phone,
  newPhone => {
    if (newPhone) {
      phoneNumber.value = removeCountryCode(newPhone, currentPhoneCode.value)
    }
  }
)
watch(
  () => props.phoneLocale,
  newLocale => {
    selectedPhoneLocale.value = newLocale

    if (newLocale && phoneNumber.value) {
      const sanitized = phoneNumber.value.replaceAll(/\s/g, '')
      if (sanitized) {
        setValidationInProgress(true)
        numberValidation({
          locale: newLocale,
          phoneNumber: sanitized,
        })
      }
    }
  }
)

onMounted(() => {
  phoneNumber.value = removeCountryCode(props.phone, currentPhoneCode.value)

  const fieldsToValidate = [
    { name: 'email', value: props.email },
    { name: 'homepageUrl', value: props.web },
    { name: 'phoneNumber', value: props.phone },
  ]
  fieldsToValidate.forEach(item => {
    if (item.name === 'phoneNumber') {
      validatePhoneNumber(item.name, item.value)
    }
    const validationFunctions = getValidations(item.name)
    const error = validate(item.value, validationFunctions)
    setValidation({
      name: item.name,
      value: error !== undefined ? error : false,
    })
  })
})

const getErrorMessage = error => {
  if (error.includes('$example')) {
    if (localeExample()) {
      return error.replace('$example', localeExample().replaceAll(' ', ''))
    } else {
      return error.split('.')[0]
    }
  } else {
    return error
  }
}

const numberValidation = async ({ locale, phoneNumber }) => {
  try {
    const response = await OrganisationManagementService.validatePhoneNumber({
      nationalNumber: phoneNumber,
      regionCode: locale,
    })

    if (response?.data?.acceptedNumber) {
      setValidation({ name: 'phone', value: false })
      setValidationInProgress(false)
      phoneValidationErrors.value = []
    }
  } catch (err) {
    if (err.response?.data?.exampleNumber) {
      exampleNumber.value = err?.response?.data?.exampleNumber
    }
    setValidation({
      name: 'phone',
      value: 'invalid_phone_number',
    })
    setValidationInProgress(false)
  }
}

const validatePhoneNumber = (name, value) => {
  currentValidation.value = 'phone'
  setPhoneNumberTouched(true)

  const number = name === 'phoneNumber' ? value : props.phone
  const clearNumber = removeCountryCode(number, currentPhoneCode.value)
  const sanitized = (clearNumber || '').replaceAll(/\s/g, '')

  if (!sanitized) {
    setValidation({
      name: 'phone',
      value: 'error_field_required',
    })
  } else {
    setValidationInProgress(true)
    numberValidation({ locale: selectedPhoneLocale.value, phoneNumber: sanitized })

    const combinedPhone = combinePhoneNumber(sanitized, currentPhoneCode.value)
    setNewPropertyValues({
      name: 'phone',
      value: combinedPhone,
    })
    phoneValidationErrors.value = []
  }
}

const onSelect = (name, value) => {
  if (name === 'phoneRegionCode') {
    selectedPhoneLocale.value = value

    setNewPropertyValues({
      name: 'phoneRegionCode',
      value: value,
    })

    if (phoneNumber.value && phoneNumber.value.trim()) {
      const newCountryCode = phoneCodes.value.find(item => item.value === value)
      const sanitized = phoneNumber.value.replaceAll(/\s/g, '')
      const combinedPhone = combinePhoneNumber(sanitized, newCountryCode)

      setNewPropertyValues({
        name: 'phone',
        value: combinedPhone,
      })

      currentValidation.value = 'phone'
      setPhoneNumberTouched(true)
      setValidationInProgress(true)

      numberValidation({
        locale: value,
        phoneNumber: sanitized,
      })
    }
  }
}

const inputChange = (name, value) => {
  setPhoneNumberTouched(true)
  const numericValue = value.replace(/\D/g, '')
  phoneNumber.value = numericValue
  validatePhoneNumber(name, numericValue)
}

const onKeyPress = event => {
  const charCode = event.which ? event.which : event.keyCode
  if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 32) {
    event.preventDefault()
  }
}

const getValidations = name => {
  if (name === 'email') {
    return [required, emailAddress]
  } else if (name === 'homepageUrl') {
    return [url, latinChars, minLength]
  } else {
    return []
  }
}

const localeExample = () => {
  const countryCode = currentPhoneCode.value?.label?.match(/\+\d+/)?.[0]
  if (!countryCode || !exampleNumber.value) return ''
  return `${exampleNumber.value}`
}

const setValue = (name, value) => {
  if (name === 'phoneNumber') {
    validatePhoneNumber(name, value)
  } else {
    const validationFunctions = getValidations(name)
    const error = validate(value, validationFunctions)
    setValidation({ name, value: error !== undefined ? error : false, isFormValid: error === undefined })
    setNewPropertyValues({ name, value })
  }
}
</script>
<style scoped>
.divider {
  border-right: 1px solid var(--tw-color-gray-300);
  height: 100%;
  min-height: 200px;
}
</style>
